import React from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useI18n } from '@/i18n';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { appConfig } from '@/config/app-config';
import { odooClient } from '@/services/odoo-client';
import {
  LogIn, Globe, Database, User, Lock, Phone, Mail,
  Eye, EyeOff, Shield, Zap, Headphones, ArrowRight, ArrowLeft,
  KeyRound, Fingerprint, Sparkles
} from 'lucide-react';

/**
 * Normalizes phone number format matching Flutter Android client
 */
function normalizePhoneNumber(phone: string): string {
  // Remove all non-digit characters except possibly a leading plus
  let cleaned = phone.replace(/[^\d+]/g, '');

  if (cleaned.startsWith('+20')) {
    cleaned = cleaned.substring(3);
  } else if (cleaned.startsWith('0020')) {
    cleaned = cleaned.substring(4);
  } else if (cleaned.startsWith('20') && cleaned.length > 10) {
    // Handle cases like 20100...
    cleaned = cleaned.substring(2);
  }

  // If it starts with 1 and length is 10, it's likely a mobile number missing leading 0
  if (cleaned.length === 10 && cleaned.startsWith('1')) {
    cleaned = '0' + cleaned;
  }

  return cleaned;
}

/**
 * Checks if the input string is a potential phone number
 */
function isPhoneNumber(input: string): boolean {
  const trimmed = input.trim();
  // Match digits or starts with + followed by digits
  return /^\+?[0-9\s\-()]+$/.test(trimmed) && trimmed.replace(/[^\d]/g, '').length >= 7;
}

export const LoginForm: React.FC = () => {
  const { t, dir } = useI18n();
  const { login, isLoading, error, clearError } = useAuthStore();

  // Form fields
  const [identifier, setIdentifier] = React.useState(appConfig.lastLoginIdentifier || '');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [rememberMe, setRememberMe] = React.useState(appConfig.rememberMe);
  
  // Custom states
  const [isResolvingPhone, setIsResolvingPhone] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [loginAttempted, setLoginAttempted] = React.useState(false);

  const handleIdentifierChange = (value: string) => {
    setIdentifier(value);
    clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAnimating(true);
    setLoginAttempted(true);
    clearError();

    const trimmedIdentifier = identifier.trim();
    let loginUsername = trimmedIdentifier;

    try {
      // 1. Detect if it's a phone number
      const isPhone = isPhoneNumber(trimmedIdentifier);
      
      if (isPhone) {
        setIsResolvingPhone(true);
        const normalized = normalizePhoneNumber(trimmedIdentifier);
        
        // Attempt Service Account Lookup
        const resolvedEmail = await odooClient.lookupLoginByPhone(normalized);
        
        if (resolvedEmail) {
          loginUsername = resolvedEmail;
        } else {
          // No fallback - show error instead of guessing domain
          setIsResolvingPhone(false);
          setIsAnimating(false);
          setLoginAttempted(false);
          throw new Error(isArabic ? 'لم يتم العثور على حساب مرتبط بهذا الرقم. حاول استخدام البريد الإلكتروني' : 'No account found for this phone number. Try using your email instead');
        }
        setIsResolvingPhone(false);
      }

      // Save identifier for remember me
      if (rememberMe) {
        appConfig.lastLoginIdentifier = identifier;
        appConfig.rememberMe = true;
      } else {
        appConfig.lastLoginIdentifier = '';
        appConfig.rememberMe = false;
      }

      // Retrieve connection credentials securely from env configuration keys
      const odooUrl = appConfig.odooUrl;
      const odooDb = appConfig.odooDb;

      await login(odooUrl, odooDb, loginUsername, password);
    } catch (err) {
      // Error is gracefully caught and shown via store
    } finally {
      setIsResolvingPhone(false);
      setIsAnimating(false);
    }
  };

  const isArabic = dir === 'rtl';
  const ArrowIcon = isArabic ? ArrowLeft : ArrowRight;

  // Real-time automatic recognition logic
  const isPhoneInput = identifier.trim() !== '' && isPhoneNumber(identifier);
  const isEmailInput = identifier.trim() !== '' && !isPhoneInput && identifier.includes('@');

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-background">
      {/* Background Glows */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -z-10 animate-pulse pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -z-10 animate-pulse pointer-events-none" style={{ animationDelay: '2s' }} />

      {/* ===== Left Panel - Branding ===== */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-gradient-to-br from-primary via-primary/95 to-primary/80 text-primary-foreground">
        {/* Animated background shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/3 -right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute -bottom-20 left-1/4 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
          {/* Grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          {/* Logo */}
          <div className="mb-10">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-2xl">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>

          <h1 className="text-4xl xl:text-5xl font-bold mb-4 leading-tight">
            {t('app_name')}
          </h1>
          <p className="text-lg text-white/70 mb-12 leading-relaxed max-w-md">
            {isArabic
              ? 'نظام متكامل لإدارة تذاكر الدعم الفني والصيانة'
              : 'Complete ticket management and maintenance support system'}
          </p>

          {/* Feature cards */}
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <Zap className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">{isArabic ? 'إدارة سريعة' : 'Fast Management'}</p>
                <p className="text-xs text-white/60">{isArabic ? 'تتبع التذاكر والبلاغات بسهولة' : 'Track tickets and reports easily'}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <Headphones className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">{isArabic ? 'دعم فني متميز' : 'Premium Support'}</p>
                <p className="text-xs text-white/60">{isArabic ? 'فريق دعم متخصص على مدار الساعة' : 'Specialized support team 24/7'}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-sm">{isArabic ? 'اتصال آمن' : 'Secure Connection'}</p>
                <p className="text-xs text-white/60">{isArabic ? 'ربط مباشر ومشفر مع نظام Odoo' : 'Direct and secure connection with Odoo'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Right Panel - Login Form ===== */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md animate-fade-in space-y-6">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-primary flex items-center justify-center mx-auto shadow-glow mb-4">
              <Shield className="w-7 h-7 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold">{t('app_name')}</h1>
          </div>

          {/* Glow indicator for automatic detection */}
          {isPhoneInput && (
            <div className="bg-primary/10 text-primary border border-primary/20 rounded-full px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5 mx-auto w-max animate-bounce">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{isArabic ? 'تم التعرف تلقائياً: رقم هاتف' : 'Auto-detected: Phone number'}</span>
            </div>
          )}
          {isEmailInput && (
            <div className="bg-primary/10 text-primary border border-primary/20 rounded-full px-4 py-1.5 text-xs font-semibold flex items-center gap-1.5 mx-auto w-max animate-bounce">
              <Sparkles className="w-3.5 h-3.5" />
              <span>{isArabic ? 'تم التعرف تلقائياً: بريد إلكتروني' : 'Auto-detected: Email address'}</span>
            </div>
          )}

          {/* Form Card */}
          <div className="bg-card/75 backdrop-blur-md rounded-3xl shadow-2xl border border-border/80 p-8 space-y-6">
            {/* Header */}
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
                <Fingerprint className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight">{t('login')}</h2>
              <p className="text-sm text-muted-foreground">
                {isArabic ? 'سجل دخولك للمتابعة' : 'Sign in to continue'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Unified Identifier Field */}
              <div className="space-y-2">
                <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
                  {isPhoneInput ? (
                    <><Phone className="w-4 h-4 text-primary" /> {isArabic ? 'رقم الهاتف المكتشف' : 'Detected Phone Number'}</>
                  ) : isEmailInput ? (
                    <><Mail className="w-4 h-4 text-primary" /> {isArabic ? 'البريد الإلكتروني المكتشف' : 'Detected Email'}</>
                  ) : (
                    <><User className="w-4 h-4 text-muted-foreground" /> {isArabic ? 'البريد الإلكتروني أو رقم الهاتف' : 'Email or Phone Number'}</>
                  )}
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    value={identifier}
                    onChange={(e) => handleIdentifierChange(e.target.value)}
                    placeholder={isArabic ? 'أدخل البريد أو رقم الهاتف' : 'Enter email or phone number'}
                    dir="ltr"
                    className="h-12 pl-11 pr-4 bg-background/50 focus-visible:ring-primary/20 focus-visible:border-primary/50 transition-all rounded-xl text-base"
                    required
                    disabled={isLoading || isResolvingPhone}
                  />
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground flex items-center justify-center w-5 h-5">
                    {isPhoneInput ? (
                      <Phone className="w-5 h-5 text-primary animate-scale-in" />
                    ) : isEmailInput ? (
                      <Mail className="w-5 h-5 text-primary animate-scale-in" />
                    ) : (
                      <User className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </div>

              {/* Password Field with Show/Hide */}
              <div className="space-y-2">
                <label className="text-sm font-semibold flex items-center gap-2 text-foreground">
                  <Lock className="w-4 h-4 text-muted-foreground" />
                  {t('password')}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); clearError(); }}
                    placeholder="••••••••"
                    dir="ltr"
                    className="h-12 pl-11 pr-11 bg-background/50 focus-visible:ring-primary/20 focus-visible:border-primary/50 transition-all rounded-xl text-base"
                    required
                    disabled={isLoading || isResolvingPhone}
                  />
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                    <Lock className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    disabled={isLoading || isResolvingPhone}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me */}
              <div className="flex items-center gap-2 py-1">
                <button
                  type="button"
                  onClick={() => setRememberMe(!rememberMe)}
                  className={cn(
                    'w-5 h-5 rounded-lg border flex items-center justify-center transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20',
                    rememberMe
                      ? 'bg-primary border-primary text-primary-foreground shadow-glow-sm'
                      : 'border-input bg-background/50 hover:border-primary/50'
                  )}
                  disabled={isLoading || isResolvingPhone}
                >
                  {rememberMe && (
                    <svg className="w-3.5 h-3.5 stroke-[2.5]" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
                <span 
                  className="text-sm font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors" 
                  onClick={() => !isLoading && !isResolvingPhone && setRememberMe(!rememberMe)}
                >
                  {isArabic ? 'تذكرني على هذا الجهاز' : 'Remember me on this device'}
                </span>
              </div>

              {/* Resolving / Loading State Message */}
              {isResolvingPhone && (
                <div className="flex items-center justify-center gap-2.5 text-sm text-primary font-medium py-2 bg-primary/5 rounded-xl border border-primary/10 animate-pulse">
                  <span className="w-2.5 h-2.5 rounded-full bg-primary animate-ping shrink-0" />
                  <span>{isArabic ? 'جاري التحقق من رقم الهاتف وتأكيد الحساب...' : 'Verifying phone number and account...'}</span>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="p-3.5 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm text-center flex items-center gap-2 justify-center animate-scale-in">
                  <div className="w-2 h-2 rounded-full bg-destructive shrink-0" />
                  <span className="font-medium">{error}</span>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-12 text-base gap-2 group shadow-glow hover:shadow-glow-lg transition-all duration-300 rounded-xl"
                size="lg"
                isLoading={isLoading || isResolvingPhone}
              >
                {t('login_button')}
                {!isLoading && !isResolvingPhone && <ArrowIcon className="w-5 h-5 transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />}
              </Button>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground mt-4">
            {isArabic ? 'بوابة الدعم الفني v2.1' : 'Support Portal v2.1'} • MR | Fanny
          </p>
        </div>
      </div>
    </div>
  );
};
